/**
 * Recent activity — how this agent has actually behaved in this service
 * (task 796 placeholder; wired to live data in task 797).
 *
 * Every figure comes from `GET /agents/:agentId/journals`, whose aggregate
 * covers the whole selected window rather than the page of rows below it. The
 * panel's rule, inherited from the dashboard doctrine the source report cites:
 * **a number that cannot be resolved from the API is omitted, never guessed.**
 * That shows up in three places here — an unresolved average renders as an
 * em-dash rather than a zero, an error renders as an error rather than empty
 * tiles, and an empty window says so instead of showing four zeros that look
 * like a healthy quiet period.
 *
 * The scope is the service, not the agent globally: `collaborationConfigId` is
 * always sent, because the question this tab asks is "how does this agent behave
 * *here*", and the same agent may be busy in another service entirely.
 *
 * Task 1309: with `collaborationConfigId={null}` (the Agents page's Runs tab) the
 * panel covers every service. The filter row then gains a service picker — "All
 * services" plus the AI Services whose flows run this agent, from its dependents
 * read — which narrows the read by that service's endpoint id (task 1330; exact
 * even when another service shares its config), and the table gains a Service
 * column. With one service picked, every row names it and links into that
 * service. Under "All services" each journal row resolves to its service by the
 * run's `endpointId` first (task 1330), then by its `collaborationConfigId`
 * through the same dependents read (task 1325) only when the run has no
 * endpoint. A row whose endpoint the dependents read does not list, whose
 * config matches no service, or whose config is shared by two services with no
 * endpoint to tell them apart, shows an em-dash and links to the run's own page
 * rather than guessing a service.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAgentJournals } from '@/hooks/useAgents';
import { useDependents } from '@/hooks/useDependents';
import type { AgentJournalEntry } from '@/types';
import type { DependentEndpointRef } from '@/types/dependents';
import { runExplorerPath, serviceRunPath } from '@/components/runExplorer/runExplorerLinks';

/** The windows the pill control offers, in hours. */
const WINDOWS = [
  { label: '24 h', hours: 24 },
  { label: '7 d', hours: 24 * 7 },
  { label: '30 d', hours: 24 * 30 },
] as const;

const PAGE_SIZE = 10;

const formatLatency = (ms: number | null): string => {
  if (ms === null) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const formatTokens = (tokens: number | null): string =>
  tokens === null ? '—' : Math.round(tokens).toLocaleString();

const formatPercent = (rate: number): string => `${(rate * 100).toFixed(1)}%`;

const formatTime = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

function StatTile({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
      <div className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">{value}</div>
      <div className="text-[11px] text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  );
}

function SkeletonTile() {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
      <div className="h-5 w-12 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
      <div className="mt-1 h-3 w-16 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
    </div>
  );
}

function RunRow({
  run,
  serviceId,
  service,
}: {
  run: AgentJournalEntry;
  serviceId?: string;
  /**
   * Account-wide mode only (task 1309): the service this row ran in — the
   * picked one, or under "All services" the one its config resolves to (task
   * 1325) — or null when that cannot be resolved.
   */
  service?: DependentEndpointRef | null;
}) {
  const accountWide = service !== undefined;
  const failed = run.status === 'failure';

  return (
    <tr data-testid={`agent-run-row-${run.id}`} className="border-b border-gray-100 dark:border-gray-700/60">
      <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">{formatTime(run.startedAt)}</td>
      {accountWide && (
        <td className="py-2 pr-3 text-gray-700 dark:text-gray-300" data-testid="agent-run-service">
          {service ? (
            <Link
              to={serviceRunPath(service.id, run.collaborationId)}
              className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              {service.name}
            </Link>
          ) : (
            <span className="text-gray-400 dark:text-gray-500">—</span>
          )}
        </td>
      )}
      <td className="py-2 pr-3 tabular-nums text-gray-700 dark:text-gray-300">
        {formatLatency(run.durationMs)}
      </td>
      <td className="py-2 pr-3 tabular-nums text-gray-700 dark:text-gray-300">
        {formatTokens(run.totalTokens)}
      </td>
      <td className="py-2 pr-3">
        <span
          className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${
            failed
              ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
              : 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
          }`}
        >
          {failed ? 'failed' : 'completed'}
        </span>
        {/* The reason belongs next to the badge — a red pill with no cause is
            the least useful thing an operations view can show. */}
        {failed && run.errorSummary && (
          <span
            title={run.errorSummary}
            className="ml-2 inline-block max-w-[16rem] truncate align-bottom text-[11px] text-red-700 dark:text-red-300"
          >
            {run.errorSummary}
          </span>
        )}
      </td>
      <td className="py-2 text-right">
        {/* Journals link to their RUN — the collaboration — because that is the
            detail view this hub already owns. */}
        {accountWide ? (
          <Link
            to={
              service
                ? serviceRunPath(service.id, run.collaborationId)
                : runExplorerPath(run.collaborationId, run.collaborationId)
            }
            className="text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            View run →
          </Link>
        ) : serviceId && (
          <Link
            to={serviceRunPath(serviceId, run.collaborationId)}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            View run →
          </Link>
        )}
      </td>
    </tr>
  );
}

interface AgentActivityPanelProps {
  agentId: string;
  /**
   * Scopes the history to this service's flow. `null` covers every service
   * (the Agents page's Runs tab), adding the Service column and picker.
   */
  collaborationConfigId: string | null;
  /** The AI Service, used to build run-detail links. */
  serviceId?: string;
}

export function AgentActivityPanel({
  agentId,
  collaborationConfigId,
  serviceId,
}: AgentActivityPanelProps) {
  const [windowHours, setWindowHours] = useState<number>(WINDOWS[0].hours);
  const [limit, setLimit] = useState<number>(PAGE_SIZE);

  const accountWide = collaborationConfigId === null;
  const [pickedServiceId, setPickedServiceId] = useState<string>('');
  const { data: dependents } = useDependents('agents', agentId, accountWide);
  // Only a service whose config is known can narrow the read.
  const services = accountWide
    ? (dependents?.dependents.endpoints ?? []).filter((endpoint) => !!endpoint.collaborationConfigId)
    : [];
  const pickedService = services.find((endpoint) => endpoint.id === pickedServiceId) ?? null;
  /**
   * "All services" (task 1325): a row's config → its service. A config run by
   * more than one service maps to null — the row cannot say which one ran it.
   */
  const serviceByConfig = new Map<string, DependentEndpointRef | null>();
  for (const endpoint of services) {
    const configId = endpoint.collaborationConfigId;
    if (!configId) continue;
    serviceByConfig.set(configId, serviceByConfig.has(configId) ? null : endpoint);
  }
  /**
   * Task 1330: the run's endpoint names its service exactly. The config mapping
   * is only for a run with no endpoint; a run whose endpoint is not in the
   * dependents read is never re-attributed through its config.
   */
  const rowService = (run: AgentJournalEntry): DependentEndpointRef | null => {
    if (pickedService) return pickedService;
    if (run.endpointId) return services.find((endpoint) => endpoint.id === run.endpointId) ?? null;
    return run.collaborationConfigId ? serviceByConfig.get(run.collaborationConfigId) ?? null : null;
  };

  const { data, isLoading, isError } = useAgentJournals(agentId, {
    collaborationConfigId: accountWide ? undefined : collaborationConfigId ?? undefined,
    // The picked service by its own id (task 1330), so another service running
    // the same config does not leak into its runs.
    endpointId: accountWide ? pickedService?.id : undefined,
    window: windowHours,
    limit,
  });

  const aggregate = data?.aggregate;
  const journals = data?.journals ?? [];
  const total = data?.total ?? 0;

  /** Changing the window resets paging — "load more" within 24 h says nothing
      about how far into 30 days the User wants to read. */
  const selectWindow = (hours: number) => {
    setWindowHours(hours);
    setLimit(PAGE_SIZE);
  };

  const selectService = (id: string) => {
    setPickedServiceId(id);
    setLimit(PAGE_SIZE);
  };

  return (
    <section
      data-testid="agent-activity-panel"
      className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {accountWide
              ? 'This agent\u2019s executions across all services'
              : 'This agent\u2019s executions in this service'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {accountWide && (
            <select
              value={pickedServiceId}
              onChange={(event) => selectService(event.target.value)}
              aria-label="Service"
              data-testid="agent-activity-service-picker"
              className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              <option value="">All services</option>
              {services.map((endpoint) => (
                <option key={endpoint.id} value={endpoint.id}>
                  {endpoint.name}
                </option>
              ))}
            </select>
          )}

          <div
            role="group"
            aria-label="Activity window"
            data-testid="agent-activity-window"
            className="flex overflow-hidden rounded-md border border-gray-200 dark:border-gray-600"
          >
            {WINDOWS.map((option) => (
              <button
                key={option.hours}
                type="button"
                onClick={() => selectWindow(option.hours)}
                aria-pressed={windowHours === option.hours}
                data-testid={`agent-activity-window-${option.hours}`}
                className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                  windowHours === option.hours
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isError ? (
        <p
          data-testid="agent-activity-error"
          className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          Run history could not be loaded. No figures are shown rather than
          approximate ones.
        </p>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="agent-activity-tiles">
            {isLoading || !aggregate ? (
              <>
                <SkeletonTile />
                <SkeletonTile />
                <SkeletonTile />
                <SkeletonTile />
              </>
            ) : (
              <>
                <StatTile value={aggregate.runCount.toLocaleString()} label="Runs" />
                <StatTile value={formatLatency(aggregate.avgLatencyMs)} label="Avg latency" />
                <StatTile value={formatTokens(aggregate.avgTotalTokens)} label="Avg tokens / run" />
                <StatTile value={formatPercent(aggregate.failureRate)} label="Failure rate" />
              </>
            )}
          </div>

          {!isLoading && journals.length === 0 ? (
            <p
              data-testid="agent-activity-empty"
              className="mt-4 text-sm text-gray-500 dark:text-gray-400"
            >
              No runs in this period.
            </p>
          ) : (
            journals.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-xs" data-testid="agent-activity-table">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
                      <th className="pb-2 pr-3 font-bold">Started</th>
                      {accountWide && <th className="pb-2 pr-3 font-bold">Service</th>}
                      <th className="pb-2 pr-3 font-bold">Duration</th>
                      <th className="pb-2 pr-3 font-bold">Tokens</th>
                      <th className="pb-2 pr-3 font-bold">Status</th>
                      <th className="pb-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {journals.map((run) => (
                      <RunRow
                        key={run.id}
                        run={run}
                        serviceId={serviceId}
                        service={accountWide ? rowService(run) : undefined}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {total > journals.length && (
            <button
              type="button"
              onClick={() => setLimit((current) => current + PAGE_SIZE)}
              data-testid="agent-activity-load-more"
              className="mt-3 text-xs font-semibold text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              Load more ({journals.length} of {total})
            </button>
          )}
        </>
      )}
    </section>
  );
}

export default AgentActivityPanel;
